import { Fragment, type ReactNode } from 'react';
import { cn } from '../../lib/cn';
import { ImageZoom } from '../ui/image-zoom';
import { parseInline, parseMarkdown, type Block, type InlineNode } from './markdown';

const renderInline = (nodes: InlineNode[]): ReactNode =>
  nodes.map((node, index) => {
    const key = `${node.kind}-${index}`;
    switch (node.kind) {
      case 'strong':
        return (
          <strong key={key} className="font-semibold">
            {node.text}
          </strong>
        );
      case 'em':
        return (
          <em key={key} className="italic">
            {node.text}
          </em>
        );
      case 'code':
        return (
          <code
            key={key}
            className="rounded border border-border bg-sunken px-1 py-0.5 font-mono text-[12.5px] text-ink"
          >
            {node.text}
          </code>
        );
      case 'link':
        return (
          <a
            key={key}
            href={node.href}
            target="_blank"
            rel="noreferrer noopener"
            className="text-accent-fg underline underline-offset-2"
          >
            {node.text}
          </a>
        );
      case 'image':
        return <ImageZoom key={key} src={node.src} alt={node.alt} className="my-1" />;
      default:
        return <Fragment key={key}>{node.text}</Fragment>;
    }
  });

const HEADING_CLASS: Record<1 | 2 | 3, string> = {
  1: 'text-[15px] font-semibold text-ink',
  2: 'text-[14px] font-semibold text-ink',
  3: 'text-[13px] font-semibold text-ink-muted uppercase tracking-wide',
};

const renderBlock = (block: Block, index: number): ReactNode => {
  const key = `${block.kind}-${index}`;
  switch (block.kind) {
    case 'heading': {
      const Tag = (['h3', 'h4', 'h5'] as const)[block.level - 1] ?? 'h4';
      return (
        <Tag key={key} className={cn('mt-3 first:mt-0', HEADING_CLASS[block.level])}>
          {renderInline(block.content)}
        </Tag>
      );
    }
    case 'code':
      return (
        <pre
          key={key}
          className="overflow-x-auto rounded-md border border-border bg-sunken p-2.5 font-mono text-[12.5px] leading-relaxed text-ink"
        >
          <code>{block.code}</code>
        </pre>
      );
    case 'list':
      return block.ordered ? (
        <ol key={key} className="ml-5 list-decimal space-y-1">
          {block.items.map((item, itemIndex) => (
            <li key={itemIndex}>{renderInline(item)}</li>
          ))}
        </ol>
      ) : (
        <ul key={key} className="ml-5 list-disc space-y-1">
          {block.items.map((item, itemIndex) => (
            <li key={itemIndex}>{renderInline(item)}</li>
          ))}
        </ul>
      );
    case 'quote':
      return (
        <blockquote
          key={key}
          className="border-l-2 border-border-strong pl-2.5 text-ink-muted"
        >
          {renderInline(block.content)}
        </blockquote>
      );
    default:
      return (
        <p key={key} className="whitespace-pre-line">
          {renderInline(block.content)}
        </p>
      );
  }
};

export interface RichTextProps {
  value: string;
  className?: string;
}

/** Renders the stored Markdown of a case field. */
export const RichText = ({ value, className }: RichTextProps) => (
  <div className={cn('flex flex-col gap-2 text-[13px] leading-relaxed text-ink', className)}>
    {parseMarkdown(value).map(renderBlock)}
  </div>
);

/** Single-line variant for table cells: no block layout, just inline marks. */
export const RichTextInline = ({ value, className }: RichTextProps) => (
  <span className={className}>{renderInline(parseInline(value))}</span>
);
